import React, { useMemo, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, style, itemTemplate }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            filterBy="label,searchKey"
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            itemTemplate={itemTemplate}
            emptyFilterMessage="Không tìm thấy kết quả phù hợp"
        />
    );
};

const StandardHourDeclarationForm = ({
    isOpen,
    onClose,
    onSubmit,
    formData,
    setFormData,
    isEditing,
    users,
    categories = [],
    readOnly = false
}) => {
    const userOptions = useMemo(() => {
        return users.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || u.email || '';
            const dept = u.DepartmentName || '';
            const displayLabel = email ? `${fullName} - ${email}` : fullName;
            const searchStr = superCleanString(`${fullName} ${email} ${dept}`);

            return { label: displayLabel, value: u.Id, searchKey: searchStr, fullName: fullName, email: email, dept: dept };
        });
    }, [users]);

    const userItemTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>{option.email} {option.dept ? ` - ${option.dept}` : ''}</span>
            </div>
        );
    };

    const categoryOptions = useMemo(() => {
        return (categories || []).map(cat => ({
            label: `${cat.Name} (${cat.Score} giờ)`,
            value: cat.Name,
            score: cat.Score
        }));
    }, [categories]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        if (readOnly) return;
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? '' : Number(value)) : value)
        });
    };

    const handleCategoryChange = (e) => {
        if (readOnly) return;
        const selectedValue = e.value;

        if (selectedValue) {
            const selectedOption = categoryOptions.find(opt => opt.value === selectedValue);
            setFormData({
                ...formData,
                StandardHourContent: selectedValue,
                ActualHours: selectedOption ? selectedOption.score : ''
            });
        } else {
            setFormData({
                ...formData,
                StandardHourContent: '',
                ActualHours: ''
            });
        }
    };

    const handleDateChange = (name, dateObj) => {
        if (readOnly) return;
        let dateString = '';
        if (dateObj) {
            const yyyy = dateObj.getFullYear();
            const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
            const dd = String(dateObj.getDate()).padStart(2, '0');
            dateString = `${yyyy}-${mm}-${dd}`;
        }
        handleChange({ target: { name, value: dateString, type: 'text' } });
    };

    return (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
            <div className="modal-box" style={{ width: '90%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
                <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{ margin: 0, paddingRight: '20px', lineHeight: '1.4' }}>
                        {readOnly
                            ? 'Xem chi tiết Giờ định mức'
                            : (isEditing ? 'Cập nhật Mục giờ định mức' : 'Thêm mới Mục giờ định mức')
                        }
                    </h3>
                    <button className="close-btn" onClick={onClose} style={{ fontSize: '26px', lineHeight: '1', flexShrink: 0, marginTop: '-2px' }}>
                        &times;
                    </button>
                </div>
                <div className="modal-body" style={{ padding: '25px' }}>
                    <form id="standardHourForm" onSubmit={(e) => { if (readOnly) e.preventDefault(); else onSubmit(e); }}>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Chọn người dùng *</label>
                            <SearchableFocusDropdown
                                value={formData.UserId}
                                options={userOptions}
                                onChange={(e) => handleChange({ target: { name: 'UserId', value: e.value } })}
                                placeholder="Chọn người dùng"
                                className="custom-dropdown"
                                panelUniqueClass="custom-dropdown-panel-std-hour-user"
                                disabled={readOnly}
                                itemTemplate={userItemTemplate}
                            />
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Chọn mục giờ chuẩn *</label>
                            <Dropdown
                                id="StandardHourContent"
                                name="StandardHourContent"
                                value={formData.StandardHourContent || null}
                                options={categoryOptions}
                                optionLabel="label"
                                optionValue="value"
                                onChange={handleCategoryChange}
                                placeholder="Vui lòng chọn mục giờ chuẩn"
                                filter
                                filterPlaceholder="Nhập từ khóa để tìm kiếm"
                                disabled={readOnly}
                                style={{
                                    width: '100%',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    padding: '10px 10px'
                                }}
                                panelStyle={{ maxWidth: '650px' }}
                                itemTemplate={(option) => (
                                    <div style={{ whiteSpace: 'normal', lineHeight: '1.5' }}>
                                        {option.label}
                                    </div>
                                )}
                                emptyMessage="Không có dữ liệu"
                                emptyFilterMessage="Không tìm thấy kết quả phù hợp"
                                required
                            />
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Giờ chuẩn thực tế *</label>
                            <input
                                type="number"
                                name="ActualHours"
                                className="form-input"
                                value={formData.ActualHours ?? ''}
                                onChange={handleChange}
                                required
                                placeholder="0"
                                disabled={readOnly}
                                style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
                            />
                        </div>

                        <div className="form-grid-2" style={{ marginBottom: '20px' }}>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Ngày bắt đầu *</label>
                                <Calendar
                                    id="StartDate"
                                    name="StartDate"
                                    value={(formData.StartDate && !isNaN(new Date(formData.StartDate).getTime())) ? new Date(formData.StartDate) : null}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    onChange={(e) => handleDateChange('StartDate', e.value)}
                                    style={{ width: '100%' }}
                                    appendTo={document.body}
                                    panelStyle={{ marginTop: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', borderRadius: '8px', zIndex: 99999 }}
                                    inputStyle={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRight: 'none', borderRadius: '6px 0 0 6px', fontSize: '14px', outline: 'none' }}
                                    disabled={readOnly}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Ngày kết thúc *</label>
                                <Calendar
                                    id="EndDate"
                                    name="EndDate"
                                    value={(formData.EndDate && !isNaN(new Date(formData.EndDate).getTime())) ? new Date(formData.EndDate) : null}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    onChange={(e) => handleDateChange('EndDate', e.value)}
                                    style={{ width: '100%' }}
                                    appendTo={document.body}
                                    panelStyle={{ marginTop: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', borderRadius: '8px', zIndex: 99999 }}
                                    inputStyle={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRight: 'none', borderRadius: '6px 0 0 6px', fontSize: '14px', outline: 'none' }}
                                    disabled={readOnly}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                            <input
                                type="checkbox"
                                name="IsCurrent"
                                checked={formData.IsCurrent || false}
                                onChange={handleChange}
                                style={{ width: '18px', height: '18px', cursor: readOnly ? 'not-allowed' : 'pointer' }}
                                id="isCurrentHR"
                                disabled={readOnly}
                            />
                            <label htmlFor="isCurrentHR" style={{ margin: 0, cursor: readOnly ? 'not-allowed' : 'pointer', fontWeight: 'bold', color: '#2c3e50' }}>
                                Hiện tại
                            </label>
                        </div>

                        <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Ghi chú</label>
                            <textarea
                                name="Notes"
                                className="form-input"
                                rows="3"
                                value={formData.Notes || ''}
                                onChange={handleChange}
                                placeholder="Ghi chú"
                                disabled={readOnly}
                                style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical' }}
                            ></textarea>
                        </div>
                    </form>
                </div>
                <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 25px' }}>
                    {readOnly ? (
                        <button type="button" className="btn-submit" onClick={onClose}>Đóng</button>
                    ) : (
                        <>
                            <button type="button" className="btn-cancel" onClick={onClose}>Hủy</button>
                            <button type="submit" form="standardHourForm" className="btn-submit">Hoàn tất</button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StandardHourDeclarationForm;